import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import Discovery from "@/pages/Discovery";
import Music from "@/pages/Music";
import Events from "@/pages/Events";
import EventCalendar from "@/pages/EventCalendar";
import FoodTrucks from "@/pages/FoodTrucks";
import Vendors from "@/pages/Vendors";
import Shorts from "@/pages/Shorts";
import SubmitBusiness from "@/pages/SubmitBusiness";
import RegisterBand from "@/pages/RegisterBand";
import RegisterVenue from "@/pages/RegisterVenue";
import RegisterPromoter from "@/pages/RegisterPromoter";
import RegisterContentCreator from "@/pages/RegisterContentCreator";
import RegisterDispensary from "@/pages/RegisterDispensary";
import CannaClub from "@/pages/CannaClub";
import SubmitEvent from "@/pages/SubmitEvent";
import Contact from "@/pages/Contact";
import ArtistProfile from "@/pages/ArtistProfile";
import BusinessPortal from "@/pages/BusinessPortal";
import AdminDashboard from "@/pages/AdminDashboard";
import BandDashboard from "@/pages/BandDashboard";
import UserProfile from "@/pages/UserProfile";
import SearchPage from "@/pages/SearchPage";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import CommunityBoard from "@/pages/CommunityBoard";
import UploadPage from "@/pages/UploadPage";
import FeedPage from "@/pages/FeedPage";
import PlaylistsPage from "@/pages/PlaylistsPage";
import DrUnk from "@/pages/SoulPrime";
import RegisterGrower from "@/pages/RegisterGrower";
import GrowerProfile from "@/pages/GrowerProfile";
import RegisterExtractor from "@/pages/RegisterExtractor";
import ExtractorProfile from "@/pages/ExtractorProfile";
import ForYouPage from "@/pages/ForYouPage";
import VocalistApplication from "@/pages/VocalistApplication";
import AboutUs from "@/pages/AboutUs";
import Pricing from "@/pages/Pricing";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import BookingSuccess from "@/pages/BookingSuccess";
import SocialProfile from "@/pages/SocialProfile";
import ProfileEdit from "@/pages/ProfileEdit";
import MyNetwork from "@/pages/MyNetwork";
import VenuePage from "@/pages/VenuePage";
import VenueDirectory from "@/pages/VenueDirectory";
import VenueDashboard from "@/pages/VenueDashboard";
import InstallPrompt from "@/components/InstallPrompt";
import UpdateNotification from "@/components/UpdateNotification";
import BetaWelcomePopup from "@/components/BetaWelcomePopup";
import { AuthProvider } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";

// Auto-update check on startup
const checkForUpdates = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        // Check for updates
        await registration.update();
        
        // If there's a waiting worker, activate it
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          toast.info('App updated! Refreshing...', { duration: 2000 });
          setTimeout(() => window.location.reload(), 2000);
        }
      }
    } catch (error) {
      console.log('Update check failed:', error);
    }
  }
};

function AppRouter() {
  const location = window.location;
  // CRITICAL: Detect session_id in hash synchronously before routing
  // This prevents race conditions with ProtectedRoute/AuthProvider
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/shorts" element={<Shorts />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/soul-prime" element={<DrUnk />} />
      <Route path="/dr-unk" element={<DrUnk />} />
      <Route path="/vocalist-application" element={<VocalistApplication />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<Discovery />} />
        <Route path="feed" element={<FeedPage />} />
        <Route path="for-you" element={<ForYouPage />} />
        <Route path="music" element={<Music />} />
        <Route path="playlists" element={<PlaylistsPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="events" element={<Events />} />
        <Route path="calendar" element={<EventCalendar />} />
        <Route path="venues" element={<VenueDirectory />} />
        <Route path="venue/:slug" element={<VenuePage />} />
        <Route path="venue-dashboard" element={<VenueDashboard />} />
        <Route path="profile" element={<UserProfile />} />
        <Route path="profile/edit" element={<ProfileEdit />} />
        <Route path="network" element={<MyNetwork />} />
        <Route path="u/:userId" element={<SocialProfile />} />
        <Route path="community" element={<CommunityBoard />} />
        <Route path="food-trucks" element={<FoodTrucks />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="canna-club" element={<CannaClub />} />
        <Route path="upload" element={<UploadPage />} />
        <Route path="submit" element={<SubmitBusiness />} />
        <Route path="submit-event" element={<SubmitEvent />} />
        <Route path="register/band" element={<RegisterBand />} />
        <Route path="register/venue" element={<RegisterVenue />} />
        <Route path="register/promoter" element={<RegisterPromoter />} />
        <Route path="register/content-creator" element={<RegisterContentCreator />} />
        <Route path="register/dispensary" element={<RegisterDispensary />} />
        <Route path="register/grower" element={<RegisterGrower />} />
        <Route path="register/extractor" element={<RegisterExtractor />} />
        <Route path="register/event" element={<SubmitEvent />} />
        <Route path="grower/:growerId" element={<GrowerProfile />} />
        <Route path="extractor/:extractorId" element={<ExtractorProfile />} />
        <Route path="contact" element={<Contact />} />
        <Route path="about" element={<AboutUs />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="subscription/success" element={<SubscriptionSuccess />} />
        <Route path="booking/success" element={<BookingSuccess />} />
        <Route path="artist/:id" element={<ArtistProfile />} />
        <Route path="business" element={<BusinessPortal />} />
        <Route path="band/dashboard" element={<BandDashboard />} />
        <Route path="register" element={<Register />} />
        <Route path="login" element={<Login />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Check for updates on mount
  useEffect(() => {
    checkForUpdates();
    
    // Also check periodically (every 5 minutes)
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthProvider>
      <PlayerProvider>
        <div className="App min-h-screen bg-background overflow-x-hidden">
          <div className="noise-overlay" />
          <BrowserRouter>
            <ScrollToTop />
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" theme="dark" />
          <InstallPrompt />
          <UpdateNotification />
          <BetaWelcomePopup />
        </div>
      </PlayerProvider>
    </AuthProvider>
  );
}

export default App;
